import { useSyncExternalStore } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";

/** What the customer chose (follow-up F18): Auto, the default, follows the salon's choice, or the phone's own setting. */
export type ThemeChoice = "auto" | Theme;

interface ThemeState {
  theme: ThemeChoice;
  setTheme: (theme: ThemeChoice) => void;
}

/**
 * The appearance, stored on the device: no sign-in needed. Version 0 stored the phone's setting as
 * it was on the first visit, before there was any way to choose, so it becomes Auto.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "auto",
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: "simpro.theme",
      version: 1,
      migrate: () => ({ theme: "auto" }),
    },
  ),
);

const DARK_QUERY = "(prefers-color-scheme: dark)";

function subscribeToSystem(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function systemIsDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

/**
 * The theme on screen (FR-SA-189): the customer's own Light or Dark always wins; Auto takes the
 * salon's choice, and the phone's setting, live, when the salon has none (`null`) or has chosen
 * to follow the phone.
 */
export function useResolvedTheme(salonDefault: Theme | null = null): Theme {
  const choice = useThemeStore((state) => state.theme);
  const dark = useSyncExternalStore(subscribeToSystem, systemIsDark, () => false);
  if (choice !== "auto") return choice;
  return salonDefault ?? (dark ? "dark" : "light");
}
